import { dynamicImage } from "../Utils";
import { getCurrentUser } from "./auth";
import data from "./config";

export async function filterNonBrokenImages(imageUrls) {
  const checkImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = url;
      img.onload = () => resolve(url);
      img.onerror = () => reject(url);
    });
  };

  const results = await Promise.allSettled(imageUrls.map(checkImage));

  const validUrls = results
    .filter((result) => result.status === "fulfilled")
    .map((result) => result.value);

  return validUrls;
}

export function getProductMainImage(product) {
  // console.log(product);
  // Check if the product has images and the first image exists
  if (product.images && product.images.length > 0) {
    return product.images[0];
  }

  // Return the default image if no images are available
  return dynamicImage("user.png");
}

export function getFormattedPrice(p) {
  p = `$${parseFloat(p).toFixed(2)}`;
  return p;
}

export function getFormattedDate(date) {
  const options = { month: "short", day: "numeric", year: "2-digit" };
  const formattedDate = new Date(date).toLocaleDateString("en-US", options);
  return formattedDate;
}

export function getAPIURL() {
  const api_url = process.env.REACT_APP_API_URL;
  return api_url;
}

export function getAPIURLAWS() {
  const api_url_aws = process.env.REACT_APP_API_URL_AWS;
  return api_url_aws;
}

export function getUserID() {
  try {
    const user = getCurrentUser();
    if (!user) return null;
    return user.user.ID;
  } catch (ex) {
    return null;
  }
}

export function getSmartUser() {
  try {
    const user = getCurrentUser();
    if (!user) return null;
    return { user_id: user.user.ID, user_name: user.user.display_name };
  } catch (ex) {
    return null;
  }
}

export function getMyTcoins() {
  try {
    const user = getCurrentUser();
    if (!user) return null;
    return { user_id: user.user.ID, user_name: user.user.display_name };
  } catch (ex) {
    return null;
  }
}

export function getStoreName() {
  const VendorData = getCurrentUser();
  if (!VendorData || !VendorData.user_roles) return "";

  const { user_roles } = VendorData;

  if (user_roles.includes("administrator")) {
    return "Tealbo store";
  }

  if (
    [
      "wc_product_vendors_manager_vendor",
      "wc_product_vendors_admin_vendor",
    ].some((role) => user_roles.includes(role))
  ) {
    return getVendorSettings("name");
  }

  return "";
}

export function isVendor(VendorData = {}) {
  if (!VendorData || !VendorData.user_roles) return false;

  const { user_roles } = VendorData;

  // Check if either role exists in user_roles
  return (
    user_roles.includes("wc_product_vendors_manager_vendor") ||
    user_roles.includes("wc_product_vendors_admin_vendor")
  );
}

export function isVendorAdmin() {
  const VendorData = getCurrentUser();
  if (!VendorData || !VendorData.user_roles) return false;

  const { user_roles } = VendorData;

  // Check if either role exists in user_roles
  return user_roles.includes("wc_product_vendors_admin_vendor");
}

export function isAdminUser() {
  const VendorData = getCurrentUser();
  if (!VendorData || !VendorData.user_roles) return false;

  const { user_roles } = VendorData;

  // Check if 'administrator' role exists in user_roles
  return user_roles.includes("administrator");
}

export function getVendorID() {
  const VendorData = getCurrentUser();
  if (!VendorData || !VendorData.vendor_id) return null;

  const { vendor_id } = VendorData;
  return vendor_id;
}

export function getVendorsList() {
  const VendorData = getCurrentUser();
  if (!VendorData || !VendorData.switcher_list) return [];

  const vendor_id = getVendorID();
  const active_vendor = getVendorSettings("active_vendor", vendor_id);
  const { switcher_list } = VendorData;
  // const updatedSwitcherList = switcher_list.map((v) => {
  //   if (v.id === parseInt(active_vendor)) {
  //     v.active = true;
  //   } else {
  //     v.active = false;
  //   }
  //   return v;
  // });
  // return updatedSwitcherList;
  // console.log(updatedSwitcherList);
  return switcher_list;
}

export function getVendorUsers() {
  const VendorData = getCurrentUser();
  if (!VendorData || !VendorData.vendor_users) return [];

  const { vendor_users } = VendorData;
  return vendor_users;
}

export function getUserPermissions() {
  const VendorData = getCurrentUser();
  if (!VendorData || !VendorData.user_permissions) return [];

  const { user_permissions } = VendorData;
  return user_permissions;
}

export function getPOSCode() {
  const VendorData = getCurrentUser();
  if (!VendorData || !VendorData.pos_code) return [];

  const { pos_code } = VendorData;
  return pos_code;
}

export function getPPOMList() {
  const VendorData = getCurrentUser();
  if (!VendorData || !VendorData.ppom_list) return [];

  const { ppom_list } = VendorData;
  return ppom_list;
}

/**
 * Updates vendor data in localStorage.
 * @param {Object} newData - New data to be merged into the existing vendor data.
 */
export const setVendorData = (newData) => {
  const storageKey = "tealbo_vendor"; // Define the storage key for vendor data
  const existingData = JSON.parse(localStorage.getItem(storageKey)) || {};

  // Check if there's existing vendor data and merge it with the new data
  const updatedVendorData = {
    ...existingData.vendor_data,
    ...newData,
  };

  // console.log(newData);

  // Set the updated vendor data back to localStorage
  localStorage.setItem(
    storageKey,
    JSON.stringify({ ...existingData, vendor_data: updatedVendorData })
  );

  // Optional: Log the updated data for debugging purposes
  // console.log("Updated vendor data in localStorage:", updatedVendorData);
};

export function getVendorSettings(key, default_val = null) {
  const VendorData = getCurrentUser();
  if (!VendorData || !VendorData.vendor_data) return null;
  const settings = VendorData.vendor_data;
  return settings[key] || default_val;
}

export function getTaxRate() {
  const tax_rate = getVendorSettings("vendor_tax_rate");
  if (!tax_rate) return null;
  return {
    rate: tax_rate,
    label: "Tax",
  };
}

export const calculateCartTotal = (state) => {
  const subtotal = state.cartItems.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );

  // console.log(subtotal, state.tax, state.discount);
  // Calculate total by adding tax and subtracting discount
  const total = subtotal + parseFloat(state.tax) - parseFloat(state.discount);

  let finalTotal = total;

  // Apply redeemed points if available
  if (state.redeemPoints) {
    finalTotal -= state.redeemedPointsValue;
  }

  // Apply redeemed T-Coins if available
  if (state.redeemTCoins) {
    finalTotal -= state.redeemedTCoinsValue;
  }

  // Return final total rounded to 2 decimal places
  return parseFloat(finalTotal).toFixed(2);
};

function getFlattenedProduct(product) {
  if (!product || typeof product !== "object") return [];

  // Extract image URLs
  const images = product.images?.map((image) => image.src) || [];

  // Extract categories
  const categories =
    product.categories?.map((cat) => ({
      id: cat.id,
      name: cat.name,
    })) || [];

  // Extract product addons (assuming it is stored in metadata)
  const addonsMeta = product.meta_data?.find(
    (meta) => meta.key === "product_addons"
  );
  const addons = addonsMeta ? addonsMeta.value : [];

  const flattenedProducts = [];

  if (product.type === "variable" && Array.isArray(product.variations)) {
    // Handle variable product variations
    product.variations.forEach((variation) => {
      flattenedProducts.push({
        id: variation.id,
        name: variation.name || `${product.name} - Variant`,
        price: variation.price,
        regular_price: variation.regular_price,
        sale_price: variation.sale_price,
        stock_quantity: variation.stock_quantity || 0,
        sku: variation.sku,
        manage_stock: variation.manage_stock,
        attributes: variation.attributes || {},
        images,
        categories,
        addons,
        taxable: product.tax_status === "taxable",
      });
    });
  } else {
    // Handle simple products
    flattenedProducts.push({
      id: product.id,
      name: product.name,
      price: product.price,
      regular_price: product.regular_price,
      sale_price: product.sale_price,
      stock_quantity: product.stock_quantity || 0,
      sku: product.sku,
      manage_stock: product.manage_stock,
      images,
      categories,
      addons,
      taxable: product.tax_status === "taxable",
    });
  }

  return flattenedProducts;
}

export const getDiscountTierByPoints = (points) => {
  const { tiers = [] } = getVendorSettings("tierDiscounts") || {};

  if (!tiers.length || !points) return null;

  const sortedTiers = [...tiers].sort(
    (a, b) => parseFloat(b.points) - parseFloat(a.points)
  );

  return sortedTiers.find((tier) => points >= parseFloat(tier.points)) || null;
};

export const generateInvoiceID = () => {
  return Date.now().toString().slice(-6);
};

export const getAvgProfit = () => {
  const isEnabled = getVendorSettings("global_profit_margin_enabled") === "true";
  
  if (!isEnabled) {
    return false;
  }
  
  const percentage = getVendorSettings("global_profit_margin_percentage");
  
  if (!percentage || isNaN(parseFloat(percentage))) {
    return false;
  }
  
  return parseFloat(percentage) / 100;
};
